import { useMutation, useQuery } from '@tanstack/react-query'
import { useConexaQuery } from '@/hooks/useConexaQuery'
import {
  getScheduledConsultations,
  getPerformedConsultations,
  getCancelledConsultations,
  getGroupConsultations,
  getReservationAttendances,
  getConsultationDetails,
  getUpcomingConsultationList,
  recuseReservationConsultation,
} from './my-calls-services'
import { IConsultation, VerifyFreeAttendance } from './types'
import { verifyFreeAttendances } from '../Attendance/services'

type ConsultationType = 'scheduled' | 'performed' | 'cancelled'

export const useConsultations = (type: ConsultationType) => {
  const queryFnMap: Record<ConsultationType, () => Promise<IConsultation[]>> = {
    scheduled: getScheduledConsultations,
    performed: getPerformedConsultations,
    cancelled: getCancelledConsultations,
  }

  return useQuery(['consultation-list', type], {
    queryFn: async () => {
      const fn = queryFnMap[type]

      if (!fn) {
        throw new Error(
          'Use one of the consultation types: `scheduled`, `performed` or `cancelled`',
        )
      }

      const consultations = await fn()

      return consultations
    },
    refetchOnWindowFocus: false,
  })
}

export const useGroupConsultations = () => {
  return useQuery(['group-consultation-list'], {
    queryFn: async () => {
      const groupConsultations = await getGroupConsultations()

      return groupConsultations
    },
    refetchOnWindowFocus: false,
  })
}

export const useReservationsConsultations = () => {
  return useQuery(['reservation-consultation-list'], {
    queryFn: getReservationAttendances,
    refetchOnWindowFocus: false,
  })
}

export const useConsultationDetails = (consultationId: string | number) => {
  return useConexaQuery({
    queryKey: ['get-consultation-details', consultationId],
    service: () => getConsultationDetails(String(consultationId)),
    config: {
      enabled: Boolean(consultationId),
      refetchOnWindowFocus: false,
    },
  })
}

export const useVerifyFreeAttendances = () => {
  return useMutation({
    mutationFn: (data: VerifyFreeAttendance) => verifyFreeAttendances(data),
  })
}

export const useGetUpcomingConsultationList = () => {
  return useQuery(['upcoming-consultation-list'], {
    queryFn: getUpcomingConsultationList,
    refetchOnWindowFocus: false,
  })
}

export const useRecuseReservationConsultation = () => {
  return useMutation({
    mutationFn: recuseReservationConsultation,
  })
}
